<template>
  <div class="grade-level-summary-row mb-6">
    <v-row no-gutters>
      <v-col cols="12" class="pr-2">
        <v-card class="pa-5">
          <v-row align="center" class="vcard-header no-border pb-3">
            <v-col cols="6">
              <h2 class="text-h6 font-weight-bold text-secondary">
                {{ gradeDisplay }} Alignment Summary
              </h2>
            </v-col>
            <v-col cols="6" align="right">
              <a class="text-primary" @click.prevent="openGlossary">
                Optional Link <v-icon>mdi-chevron-right</v-icon>
              </a>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" md="4" class="pb-0 mb-0">
              <v-card class="green-card p-0 mb-0">
                <v-row no-gutters class="px-3 pt-3 pb-2">
                  <v-col cols="12">
                    <div class="card-header mb-2">
                      <h5 class="font-weight-bold">Gateway 1</h5>
                      <router-link
                        class="text-primary font-weight-bold text-uppercase"
                        :to="{
                          name: 'report-overview',
                          params: {
                            id: props.report.slug,
                          },
                        }"
                      >
                        Overview
                      </router-link>
                    </div>
                  </v-col>
                </v-row>
                <v-row no-gutters class="px-3 pb-2">
                  <v-col cols="8" align-self="end">
                    <h5 class="font-weight-regular">
                      Standards & Research-Based Practices
                    </h5>
                  </v-col>
                  <v-col cols="4" class="text-right" align-self="end">
                    <h1>91%</h1>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <div class="card-footer">
                      <span class="card-footer-text">Meets Expectations</span>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
            <v-col cols="12" md="4" class="pb-0 mb-0">
              <v-card class="orange-card p-0 mb-0">
                <v-row no-gutters class="px-3 pt-3 pb-2">
                  <v-col cols="12">
                    <div class="card-header mb-2">
                      <h5 class="font-weight-bold">Gateway 2</h5>
                      <router-link
                        class="text-primary font-weight-bold text-uppercase"
                        :to="{
                          name: 'report-overview',
                          params: {
                            id: props.report.slug,
                          },
                        }"
                      >
                        Overview
                      </router-link>
                    </div>
                  </v-col>
                </v-row>
                <v-row no-gutters class="px-3 pb-2">
                  <v-col cols="8" align-self="end">
                    <h5 class="font-weight-regular">
                      Standards & Research-Based Practices
                    </h5>
                  </v-col>
                  <v-col cols="4" class="text-right" align-self="end">
                    <h1>72%</h1>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <div class="card-footer">
                      <span class="card-footer-text"
                        >Partially Meets Expectations</span
                      >
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
            <v-col cols="12" md="4" class="pb-0 mb-0">
              <v-card class="grey-card p-0 mb-0">
                <v-row no-gutters class="px-3 pt-3 pb-2">
                  <v-col cols="12">
                    <div class="card-header mb-2">
                      <h5 class="font-weight-bold">Gateway 3</h5>
                      <router-link
                        class="text-primary font-weight-bold text-uppercase"
                        :to="{
                          name: 'report-overview',
                          params: {
                            id: props.report.slug,
                          },
                        }"
                      >
                        Overview
                      </router-link>
                    </div>
                  </v-col>
                </v-row>
                <v-row no-gutters class="px-3 pb-2">
                  <v-col cols="8" align-self="end">
                    <h5 class="font-weight-regular">
                      Standards & Research-Based Practices
                    </h5>
                  </v-col>
                  <v-col cols="4" class="text-right" align-self="end">
                    <h1>NE</h1>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <div class="card-footer">
                      <span class="card-footer-text">Not Eligible</span>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import type { Report } from '../../store';
  import { useGlossaryStore } from '../../store';

  const props = defineProps<{
    report: Report;
    selectedGrade?: string;
  }>();

  function getOrdinalSuffix(n: number): string {
    const j = n % 10,
      k = n % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  }

  const gradeWordToNumberMap: Record<string, number> = {
    first: 1,
    second: 2,
    third: 3,
    fourth: 4,
    fifth: 5,
    sixth: 6,
    seventh: 7,
    eighth: 8,
    ninth: 9,
    tenth: 10,
    eleventh: 11,
    twelfth: 12,
  };

  const gradeDisplay = computed(() => {
    if (!props.selectedGrade) return '';

    const gradeSlug = props.selectedGrade.toLowerCase();

    if (gradeSlug === 'kindergarten' || gradeSlug === 'k') {
      return 'Kindergarten';
    }

    const gradeWord = gradeSlug.replace(/-grade$/, '');

    if (gradeWordToNumberMap[gradeWord]) {
      const gradeNumber = gradeWordToNumberMap[gradeWord];
      return `${gradeNumber}${getOrdinalSuffix(gradeNumber)}`;
    }

    if (/^\d+$/.test(gradeWord)) {
      const gradeNumber = parseInt(gradeWord, 10);
      return `${gradeNumber}${getOrdinalSuffix(gradeNumber)}`;
    }

    return props.selectedGrade;
  });

  const glossaryStore = useGlossaryStore();

  function openGlossary() {
    glossaryStore.openGlossary();
  }
</script>

<style lang="scss" scoped>
  .grade-level-summary-row {
    .vcard-header {
      border-bottom: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
      &.no-border {
        border-bottom: none;
      }
    }
  }
</style>
